/**
 * Omit Firestore-Invalid Values
 *
 * Recursively strips `undefined` and `NaN` from plain objects before Firestore writes.
 * Used when building `updateDoc` payloads so Firestore accepts nested partial updates.
 */

import { FieldValue } from 'firebase/firestore';

/**
 * Returns a copy of `data` without keys whose values are `undefined` or `NaN`.
 * Nested plain objects are cleaned recursively; empty nested objects are omitted.
 *
 * @param {Object} data Source object with optional undefined or NaN values.
 * @return {Object} Sanitized object safe for Firestore `updateDoc`.
 */
export function omitFirestoreUndefined<T extends Record<string, unknown>>(data: T): T {
  const out: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(data)) {
    if (value === undefined) {
      continue;
    }
    if (typeof value === 'number' && Number.isNaN(value)) {
      continue;
    }
    if (value instanceof FieldValue) {
      out[key] = value;
      continue;
    }
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      const nested = omitFirestoreUndefined(value as Record<string, unknown>);
      if (Object.keys(nested).length > 0) {
        out[key] = nested;
      }
      continue;
    }
    out[key] = value;
  }

  return out as T;
}
